import { FC, createRef, useEffect, useMemo, useRef, useState } from "react";
import {
  Column,
  FormController,
  MuiBox,
  MuiDateField,
  MuiGrid,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  RowValue,
} from "../../../../components";
import { ProjectSearchResultWrapper } from "./ProjectSearchResult.style";

import SearchIcon from "@mui/icons-material/Search";

import { InputAdornment } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useForm, useWatch } from "react-hook-form";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { IdNameList } from "../../../../api/services/common/models";
import MuiChipWithPopover from "../../../../components/chipWithPopover/MuiChipWithPopover";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import { MuiLink } from "../../../../components/link/MuiLink";
import { StatusBadge } from "../../../../components/statusBadge/StatusBadge";
import {
  DATE_FORMAT,
  EMPTY_VALUE_DASH,
  PAGE_URLS,
} from "../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import { MAX_LENGTH_50 } from "../../../../helpers/constants/validations";
import { LabelValue } from "../../../../helpers/types/common";
import {
  buildActionMenuItems,
  convertToUserFormat,
  createOptionsFromSet,
  filterListByFilterKeyValue,
  getFullName,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { IdNameType } from "../../../createBulkActivity/modelsAndSchemas/Models";
import {
  ContactType,
  ProjectList,
  ProjectListItem,
  ProjectListRowItem,
} from "../../../viewProject/modelsAndSchemas/models";
import {
  DropdownFilterKey,
  ProjectSearchResultFormKey,
  ProjectSearchResultRowValue,
} from "../../modelsAndSchemas/Models";
import { resultsSectionSiteCoreKeys } from "../../siteCore/SiteCoreKeys";

const pageId = "project-search-result";

interface ProjectResultFormProps {
  rows: ProjectList;
  isSearch: boolean;
  onRowSelected?: (row: RowProps) => void;
  onEditClick?: (id: string) => void;
}

export const ProjectSearchResult: FC<ProjectResultFormProps> = ({
  rows,
  isSearch,
  onRowSelected,
  onEditClick,
}) => {
  const tableRef = useRef<{
    resetPagination: (shouldResetRowsPerPage?: boolean) => void;
  }>(null);
  const {
    fetchLabel,
    commonLabels,
    fetchComponentTitle,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { hasModulePermission } = usePermissions();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const getLabel = (key: string) => {
    return fetchLabel(resultsSectionSiteCoreKeys.header, key);
  };

  const { commonARIALabel } = useNavigationSiteCore();

  const getProjectManagerName = (projectManager?: ContactType[]) => {
    if (Array.isArray(projectManager)) {
      if (projectManager.length === 0) return "";
      return projectManager
        .map((pl) =>
          getFullName(
            getValueOrBlank(pl.firstName),
            getValueOrBlank(pl.lastName),
          ),
        )
        .filter(Boolean)
        .join("| ");
    }

    if (
      projectManager &&
      typeof projectManager === "object" &&
      "name" in projectManager
    ) {
      return getValueOrBlank((projectManager as IdNameList).name);
    }

    return "";
  };

  const getProductLineNames = (productLine?: IdNameType[]) => {
    if (Array.isArray(productLine)) {
      if (productLine.length === 0) return "";
      return productLine
        .map((pl) => getValueOrBlank(pl?.name))
        .filter(Boolean)
        .join(", ");
    }

    if (
      productLine &&
      typeof productLine === "object" &&
      "name" in productLine
    ) {
      return getValueOrBlank((productLine as IdNameList).name);
    }

    return "";
  };

  const mappedRows = useMemo(
    () =>
      rows.map((item) => ({
        projectName: getValueOrBlank(item.project?.name),
        projectKeyNumber: getValueOrBlank(item.projectKeyNumber),
        clientName: getValueOrBlank(item.client?.name),
        clientCode: getValueOrBlank(item.clientCode),
        numberOfPrograms: getValueOrBlank(item.numberOfPrograms?.toString()),
        projectManager: getValueOrBlank(
          getProjectManagerName(item.projectManager),
        ),
        billingOffice: getValueOrBlank(item.billingOffice?.name),
        salesOffice: getValueOrBlank(item.salesOffice?.name),
        projectFee: getValueOrBlank(item.projectFee?.toString()),
        participantName: getValueOrBlank(item.participantName),
        createdDate: getValueOrBlank(item.createdDate),
        projectStatus: getValueOrBlank(item.projectStatus),
        poNumber: item.poNumbers?.join(","),
        productLine: getProductLineNames(item.productLine),
        id: getValueOrBlank(item.project?.id),
        isEditable: item.isEditable,
      })),
    [rows],
  );

  const [filteredResults, setFilteredResults] =
    useState<ProjectListRowItem[]>(mappedRows);
  const [uniqueFiltersOptionObj, setUniqueFiltersOptionObj] = useState<{
    [K in DropdownFilterKey]: LabelValue[];
  }>({
    [ProjectSearchResultFormKey.projectStatus]: [],
    [ProjectSearchResultFormKey.productLine]: [],
    [ProjectSearchResultFormKey.billingOffice]: [],
    [ProjectSearchResultFormKey.salesOffice]: [],
  });

  const resetPagination = (shouldResetRowsPerPage: boolean = false) => {
    if (tableRef.current) {
      tableRef.current.resetPagination(shouldResetRowsPerPage);
    }
  };

  useEffect(() => {
    reset();
    resetPagination(true);
    setFilteredResults(mappedRows);
  }, [mappedRows]);

  useEffect(() => {
    if (isNotEmpty(rows)) {
      const filterOptions = extractFilterOptions(rows);
      setUniqueFiltersOptionObj(filterOptions);
    }

    const filterList = filterListByFilterKeyValue(
      mappedRows,
      tableColumnFilterWatch,
    ) as ProjectListRowItem[];
    setFilteredResults([...filterList]);
    resetPagination();
  }, [mappedRows]);

  const extractFilterOptions = (rows: ProjectList) => {
    const projectStatusSet = new Set<string>();
    const productLineSet = new Set<string>();
    const billingOfficeSet = new Set<string>();
    const salesOfficeSet = new Set<string>();

    rows.forEach((item) => {
      addProjectStatus(item, projectStatusSet);
      addProductLine(item, productLineSet);
      addBillingOffice(item, billingOfficeSet);
      addSalesOffice(item, salesOfficeSet);
    });

    return {
      [ProjectSearchResultFormKey.projectStatus]: createOptionsFromSet(
        projectStatusSet,
        (status) =>
          fetchValueByKeyOfDropdown(
            resultsSectionSiteCoreKeys.header,
            resultsSectionSiteCoreKeys.projectStatusList,
            status,
          ) || status,
      ),
      [ProjectSearchResultFormKey.productLine]:
        createOptionsFromSet(productLineSet),
      [ProjectSearchResultFormKey.billingOffice]:
        createOptionsFromSet(billingOfficeSet),
      [ProjectSearchResultFormKey.salesOffice]:
        createOptionsFromSet(salesOfficeSet),
    };
  };

  const addProjectStatus = (item: ProjectListItem, statusSet: Set<string>) => {
    if (item[ProjectSearchResultFormKey.projectStatus]) {
      statusSet.add(
        getValueOrBlank(item[ProjectSearchResultFormKey.projectStatus]),
      );
    }
  };

  const addProductLine = (
    item: ProjectListItem,
    productLineSet: Set<string>,
  ) => {
    if (
      item[ProjectSearchResultFormKey.productLine] &&
      Array.isArray(item[ProjectSearchResultFormKey.productLine])
    ) {
      item[ProjectSearchResultFormKey.productLine]?.forEach((pl) => {
        productLineSet.add(getValueOrBlank(pl.name));
      });
    }
  };

  const addBillingOffice = (
    item: ProjectListItem,
    billingOfficeSet: Set<string>,
  ) => {
    if (item[ProjectSearchResultFormKey.billingOffice]) {
      billingOfficeSet.add(
        getValueOrBlank(item[ProjectSearchResultFormKey.billingOffice]?.name),
      );
    }
  };

  const addSalesOffice = (
    item: ProjectListItem,
    salesOfficeSet: Set<string>,
  ) => {
    if (item[ProjectSearchResultFormKey.salesOffice]) {
      salesOfficeSet.add(
        getValueOrBlank(item[ProjectSearchResultFormKey.salesOffice]?.name),
      );
    }
  };

  const { control, reset, getValues, setValue } =
    useForm<ProjectSearchResultRowValue>({
      mode: "all",
      defaultValues: {
        [ProjectSearchResultFormKey.projectName]: "",
        [ProjectSearchResultFormKey.projectKeyNumber]: "",
        [ProjectSearchResultFormKey.clientName]: "",
        [ProjectSearchResultFormKey.projectStatus]: "",
        [ProjectSearchResultFormKey.productLine]: "",
        [ProjectSearchResultFormKey.numberOfPrograms]: "",
        [ProjectSearchResultFormKey.projectManager]: "",
        [ProjectSearchResultFormKey.billingOffice]: "",
        [ProjectSearchResultFormKey.salesOffice]: "",
        [ProjectSearchResultFormKey.projectFee]: "",
        [ProjectSearchResultFormKey.poNumber]: "",
        [ProjectSearchResultFormKey.createdDate]: "",
      },
    });

  const tableColumnFilterWatch = useWatch({ control });

  useEffect(() => {
    if (isNotEmpty(rows)) {
      const allFiltersEmpty = Object.values(tableColumnFilterWatch).every(
        (v) => v === "" || v === undefined || v === null,
      );
      if (allFiltersEmpty) {
        setFilteredResults(mappedRows);
      } else {
        const filterCreatedDate = tableColumnFilterWatch.createdDate
          ? dayjs(
              tableColumnFilterWatch.createdDate,
              userCountryDateFormat,
            ).format(DATE_FORMAT.YYYY_MM_DD_HYPHEN)
          : "";
        const filterList = filterListByFilterKeyValue(mappedRows, {
          ...tableColumnFilterWatch,
          createdDate: filterCreatedDate,
        }) as ProjectListRowItem[];
        setFilteredResults(filterList);
      }
      resetPagination();
    }
  }, [tableColumnFilterWatch]);

  const getStatusSiteCoreValue = (status: string): string => {
    return fetchValueByKeyOfDropdown(
      resultsSectionSiteCoreKeys.header,
      resultsSectionSiteCoreKeys.projectStatusList,
      status,
    );
  };
  const columns: Column[] = [
    {
      id: ProjectSearchResultFormKey.projectName,
      label: getLabel(resultsSectionSiteCoreKeys.projectNameTableColumn),
      render: (row: RowProps) => (
        <MuiLink
          data-testid="project-name-link"
          href={`${PAGE_URLS.VIEW_PROJECT}/${row.id}`}
          isOpenNewTab
        >
          <MuiBox data-testid="project-name-link">
            <MuiEllipseLabel
              data-testid="project-name-link"
              labelTypoClassName="Link project-name-link"
              text={row.projectName}
              labelTypoVariant="h3"
              placement="bottom-start"
            />
          </MuiBox>
        </MuiLink>
      ),
      isSortVisible: true,
    },
    {
      id: ProjectSearchResultFormKey.projectKeyNumber,
      label: getLabel(resultsSectionSiteCoreKeys.projectKeyTableColumn),
      isSortVisible: true,
    },
    {
      id: ProjectSearchResultFormKey.clientName,
      label: getLabel(resultsSectionSiteCoreKeys.clientNameTableColumn),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiTypography>{row.clientName}</MuiTypography>
      ),
    },
    {
      id: ProjectSearchResultFormKey.projectStatus,
      label: getLabel(resultsSectionSiteCoreKeys.projectStatusTableColumn),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-status",
      render: (row: RowProps) => (
        <StatusBadge
          status={row[ProjectSearchResultFormKey.projectStatus]}
          label={getStatusSiteCoreValue(
            row[ProjectSearchResultFormKey.projectStatus],
          )}
        />
      ),
    },
    {
      id: ProjectSearchResultFormKey.productLine,
      label: getLabel(resultsSectionSiteCoreKeys.productLineTableColumn),
      isSortVisible: true,
      render: (row: RowProps) => {
        const gridRef = createRef<HTMLDivElement>();
        const chipOptions = row.productLine.split(",").map((item: string) => ({
          label: item,
          value: item,
        }));
        return (
          <MuiChipWithPopover
            data={chipOptions}
            handleDelete={() => {}}
            isEditable={false}
            headerLabel={getLabel(
              resultsSectionSiteCoreKeys.productLineTableColumn,
            )}
            gridRef={gridRef}
            countToWrap={1}
            showMoreARIALabel={commonARIALabel.showNMore}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            closePopoverARIALabel={commonARIALabel.closePopover}
          />
        );
      },
    },
    {
      id: ProjectSearchResultFormKey.numberOfPrograms,
      label: getLabel(resultsSectionSiteCoreKeys.numberOfProgramsTableColumn),
      isSortVisible: true,
    },
    {
      id: ProjectSearchResultFormKey.projectManager,
      label: getLabel(resultsSectionSiteCoreKeys.projectManagerTableColumn),
      isSortVisible: true,
      render: (row: RowProps) => {
        const gridRef = createRef<HTMLDivElement>();
        const chipOptions = row.projectManager
          .split("|")
          .map((item: string) => ({
            label: item,
            value: item,
          }));
        return (
          <MuiChipWithPopover
            data={chipOptions}
            handleDelete={() => {}}
            isEditable={false}
            headerLabel={getLabel(
              resultsSectionSiteCoreKeys.productLineTableColumn,
            )}
            gridRef={gridRef}
            countToWrap={1}
            showMoreARIALabel={commonARIALabel.showNMore}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            closePopoverARIALabel={commonARIALabel.closePopover}
          />
        );
      },
    },
    {
      id: ProjectSearchResultFormKey.billingOffice,
      label: getLabel(resultsSectionSiteCoreKeys.billingOfficeTableColumn),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiTypography>{row.billingOffice}</MuiTypography>
      ),
    },
    {
      id: ProjectSearchResultFormKey.salesOffice,
      label: getLabel(resultsSectionSiteCoreKeys.salesOfficeTableColumn),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiTypography>{row.salesOffice}</MuiTypography>
      ),
    },
    {
      id: ProjectSearchResultFormKey.projectFee,
      label: getLabel(resultsSectionSiteCoreKeys.projectFeeTableColumn),
      isSortVisible: true,
    },
    {
      id: ProjectSearchResultFormKey.poNumber,
      label: getLabel(resultsSectionSiteCoreKeys.poNumberTableColumn),
      isSortVisible: true,
      considerSortAsString: true,
      render: (row: RowProps) => {
        if (isEmpty(row.poNumber)) {
          return <></>;
        }
        const gridRef = createRef<HTMLDivElement>();
        const chipOptions = row.poNumber.split(",").map((item: string) => ({
          label: item,
          value: item,
        }));
        return (
          <MuiChipWithPopover
            data={chipOptions}
            handleDelete={() => {}}
            isEditable={false}
            headerLabel={getLabel(
              resultsSectionSiteCoreKeys.poNumberTableColumn,
            )}
            gridRef={gridRef}
            countToWrap={1}
            showMoreARIALabel={commonARIALabel.showNMore}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            closePopoverARIALabel={commonARIALabel.closePopover}
          />
        );
      },
    },
    {
      id: ProjectSearchResultFormKey.createdDate,
      label: getLabel(resultsSectionSiteCoreKeys.createdDateTableColumn),
      isSortVisible: true,
      render: (row: RowProps) => {
        if (row?.createdDate) {
          return (
            <MuiBox>
              <MuiTypography variant="h3">
                {isNotEmpty(row.createdDate)
                  ? convertToUserFormat(row.createdDate, userCountryDateFormat)
                  : EMPTY_VALUE_DASH}
              </MuiTypography>
            </MuiBox>
          );
        }
        return <></>;
      },
    },
    {
      id: "action",
      label: getLabel(resultsSectionSiteCoreKeys.actionTableColumn),
      render: (row: RowProps) => {
        const menuItems = buildActionMenuItems(
          row,
          [
            {
              show: (row) => row?.isEditable,
              create: (row) => ({
                onClick: () => {
                  onEditClick?.(row.id);
                },
                dataTestId: "edit",
                label: commonLabels.edit,
                ariaLabel: commonLabels.edit,
              }),
            },
          ],
          hasModulePermission,
        );

        return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
      },
    },
  ];

  const renderFilterTextSearch = (name: keyof ProjectSearchResultRowValue) => {
    return (
      <FormController key={name} control={control} name={name}>
        <MuiTextField
          id={`${pageId}-${name}`}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          }
          aria-label={commonLabels.search}
          showCloseIcon={true}
          data-testid="company-input"
          maxLength={MAX_LENGTH_50}
          placeholder={commonLabels.search}
          shouldTrimSpaces={false}
          requiredARIALabel={commonARIALabel.required}
        />
      </FormController>
    );
  };

  const renderFilterSelectDropdown = (name: DropdownFilterKey) => {
    const options = uniqueFiltersOptionObj[name];
    return (
      <FormController key={name} control={control} name={name}>
        <MuiSelect
          label=""
          visibleDefaultSelectOption={true}
          options={options}
          value=""
          id={`${pageId}-${name}`}
          placeholder={commonLabels.selectPlaceHolder}
          requiredARIALabel={commonARIALabel.required}
        />
      </FormController>
    );
  };

  const tableHeaderForm = [
    renderFilterTextSearch(ProjectSearchResultFormKey.projectName),
    renderFilterTextSearch(ProjectSearchResultFormKey.projectKeyNumber),
    renderFilterTextSearch(ProjectSearchResultFormKey.clientName),
    renderFilterSelectDropdown(ProjectSearchResultFormKey.projectStatus),
    renderFilterSelectDropdown(ProjectSearchResultFormKey.productLine),
    renderFilterTextSearch(ProjectSearchResultFormKey.numberOfPrograms),
    renderFilterTextSearch(ProjectSearchResultFormKey.projectManager),
    renderFilterSelectDropdown(ProjectSearchResultFormKey.billingOffice),
    renderFilterSelectDropdown(ProjectSearchResultFormKey.salesOffice),
    renderFilterTextSearch(ProjectSearchResultFormKey.projectFee),
    renderFilterTextSearch(ProjectSearchResultFormKey.poNumber),
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      key={ProjectSearchResultFormKey.createdDate}
    >
      <MuiDateField
        format={userCountryDateFormat}
        name={ProjectSearchResultFormKey.createdDate}
        clearable
        value={
          getValues(ProjectSearchResultFormKey.createdDate) !== ""
            ? dayjs(
                getValues(ProjectSearchResultFormKey.createdDate),
                userCountryDateFormat,
              )
            : null
        }
        onChange={(newValue) => {
          setValue(
            ProjectSearchResultFormKey.createdDate,
            newValue?.format(userCountryDateFormat) ?? "",
          );
        }}
        requiredARIALabel={commonARIALabel.required}
        selectDateARIALabel={commonARIALabel.selectDate}
      />
    </LocalizationProvider>,
    <></>,
  ];

  const tableHeaderLabel = isSearch
    ? fetchComponentTitle(resultsSectionSiteCoreKeys.header)
    : getLabel(resultsSectionSiteCoreKeys.allProjectsSectionHeader);

  return (
    <ProjectSearchResultWrapper>
      <MuiGrid container data-testid="project-result">
        <MuiGrid size={{ xs: 12 }} className="form-header">
          <MuiTypography variant="h2SemiBold" className="form-header-left">
            {tableHeaderLabel}
          </MuiTypography>
          {rows.length > 0 && (
            <MuiTypography variant="h3SemiBold" className="selected-count">
              {filteredResults.length}
            </MuiTypography>
          )}
        </MuiGrid>
        <MuiGrid container>
          <MuiGrid size={{ xs: 12 }} className="header-right">
            <MuiTable
              ref={tableRef}
              pageId={pageId}
              tableHeaderForm={tableHeaderForm}
              columns={columns}
              tableResults={
                [...filteredResults] as unknown as Record<string, RowValue>[]
              }
              noDataInApi={isSearch}
              onRowSelected={
                onRowSelected as ((row: RowProps) => void) | undefined
              }
              emptyMessage={commonLabels.noResult}
              noResultMessage={commonLabels.searchCriteria}
              priorityResults={[]}
              isLastColumnSticky={true}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiGrid>
    </ProjectSearchResultWrapper>
  );
};
